import { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Mail, Database, Server, Lock } from 'lucide-react';
import ModalShell from '../ui/ModalShell';
import usePotvrzeni from '../ui/usePotvrzeni';
import KDoreseniRadek, { KDoreseniSeznam } from '../layout/KDoreseni';
import { muze } from '../../lib/moduly';
import {
    SECTION_COLOR, CERVENA, ZELENA, nacti, posli, tlumene, cara,
    Nadpis, Pole, RadekPanelu, Tlacitko, PatickaModalu,
} from './ui';

/**
 * Správa → Systém. Karta (aplikace, zdravotní kontroly, report, databáze)
 * + třetí sloupec se souhrnem kontrol a „K dořešení" (norma §1f). Na mobilu
 * jeden sloupec, „K dořešení" jako řádek nad kartou (§1g).
 *
 * Dřív: Nastavení → Systém = jedno pole (e-mail personalisty, patří Lidem)
 * a DB sync. Příjemce zdravotního reportu šel změnit jen v databázi.
 */
export default function SpravaSystem({ user, isDark, isMobile, cardStyle, inputStyle, rezervaDole }) {
    const upravuje = muze(user, 'sprava.system', 'upravuje');
    const [info, setInfo] = useState(null);
    const [historie, setHistorie] = useState([]);
    const [zdravi, setZdravi] = useState(null);
    const [bezi, setBezi] = useState(false);
    const [chyba, setChyba] = useState(null);
    const [upravaReportu, setUpravaReportu] = useState(false);
    const [upravaPinu, setUpravaPinu] = useState(false);
    const [potvrdit, potvrzeni] = usePotvrzeni(isDark);

    useEffect(() => {
        nacti('/api/sprava/system').then(setInfo).catch((e) => setChyba(e.message));
        fetch(`/version.json?_=${Date.now()}`, { cache: 'no-store' }).then((r) => r.json())
            .then((v) => setHistorie((v?.history || []).slice(0, 5))).catch(() => {});
    }, []);

    const spustKontroly = useCallback(() => {
        setBezi(true);
        nacti('/api/sprava/system/zdravi').then(setZdravi).catch((e) => setChyba(e.message)).finally(() => setBezi(false));
    }, []);
    useEffect(() => { spustKontroly(); }, [spustKontroly]);

    // Co nehraje — jedna funkce pro panel i mobilní řádek (norma §1g)
    const kDoreseni = useMemo(() => (zdravi?.kontroly || [])
        .filter((k) => k.stav !== 'ok')
        .map((k) => ({ id: `${k.skupina}-${k.nazev}`, problem: k.stav === 'chyba', titulek: `${k.skupina} · ${k.nazev}`, popis: [k.hodnota, k.detail].filter(Boolean).join(' — ') })),
    [zdravi]);

    const syncDb = async () => {
        if (!(await potvrdit('Stáhnout databázi z produkce?', { text: 'Lokální databáze se celá přepíše kopií z produkce.', potvrdit: 'Stáhnout' }))) return;
        try { await posli('/api/system/db-sync', 'POST', {}); } catch (e) { setChyba(e.message); }
    };

    const ml = tlumene(isDark);
    const karta = (
        <div className={isMobile ? '' : 'rounded-xl'} style={{ ...(isMobile ? {} : cardStyle), padding: isMobile ? '4px 16px 16px' : '18px 22px', flex: 1, minWidth: 0 }}>
            {chyba && <p className="text-sm" style={{ color: CERVENA, margin: '0 0 12px' }}>{chyba}</p>}

            <Nadpis isDark={isDark}>Aplikace</Nadpis>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, minmax(0, 220px))', columnGap: 24 }}>
                <Pole popisek="Verze" isDark={isDark}>{info?.verze}</Pole>
                <Pole popisek="Prostředí" isDark={isDark}>{info?.prostredi}</Pole>
            </div>
            {/* Firemní telefony rozvozců v kiosku: PIN odemyká skryté menu (vypnout kiosek) */}
            <div className="flex items-end" style={{ gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                    <Pole popisek="Admin PIN kiosku mobilní aplikace" isDark={isDark}>
                        {info ? (info.kiosek_pin ? 'nastavený' : <span style={{ color: ml }}>není — kiosek na telefonu nejde vypnout</span>) : null}
                    </Pole>
                </div>
                {upravuje && <Tlacitko isDark={isDark} Icon={Lock} onClick={() => setUpravaPinu(true)}>{info?.kiosek_pin ? 'Změnit' : 'Nastavit'}</Tlacitko>}
            </div>
            {historie.length > 0 && (
                <div style={{ marginTop: 6 }}>
                    <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '4px 0 6px' }}>Poslední změny</p>
                    {historie.map((h) => (
                        <div key={h.version} className="flex" style={{ gap: 12, padding: '7px 0', borderTop: `1px solid ${cara(isDark)}`, fontSize: 12.5 }}>
                            <span style={{ width: 70, flexShrink: 0, fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)', fontWeight: 600 }}>{h.version}</span>
                            <span style={{ color: ml, minWidth: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{h.description}</span>
                        </div>
                    ))}
                </div>
            )}

            <Nadpis isDark={isDark} mt={24} vpravo={(
                <Tlacitko isDark={isDark} Icon={RefreshCw} onClick={spustKontroly} disabled={bezi}>{bezi ? 'Kontroluji…' : 'Znovu zkontrolovat'}</Tlacitko>
            )}>Zdravotní kontroly</Nadpis>
            {!zdravi && <p className="text-sm" style={{ color: ml }}>{bezi ? 'Spouštím kontroly…' : '—'}</p>}
            {zdravi && <SeznamKontrol kontroly={zdravi.kontroly} isDark={isDark} isMobile={isMobile} />}

            <Nadpis isDark={isDark} mt={24}>Denní zdravotní report</Nadpis>
            <div className="flex items-end" style={{ gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                    <Pole popisek="Příjemce" isDark={isDark}>
                        {info?.prijemce_reportu || (info ? <span>{info.prijemce_vychozi} <span style={{ color: ml }}>· výchozí</span></span> : null)}
                    </Pole>
                </div>
                {upravuje && !isMobile && <Tlacitko isDark={isDark} Icon={Mail} onClick={() => setUpravaReportu(true)}>Změnit</Tlacitko>}
            </div>

            {info?.je_lokal && (
                <>
                    <Nadpis isDark={isDark} mt={24}>Databáze</Nadpis>
                    <p className="text-sm" style={{ color: ml, margin: '0 0 10px' }}>Jen na lokálu: přepíše lokální databázi kopií z produkce.</p>
                    <Tlacitko isDark={isDark} Icon={Database} onClick={syncDb}>Stáhnout z produkce</Tlacitko>
                </>
            )}
            {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
        </div>
    );

    return (
        <div className="flex" style={{ gap: 16, alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row', minHeight: 0 }}>
            {isMobile && kDoreseni.length > 0 && (
                <div style={{ padding: '8px 16px 0', width: '100%' }}>
                    <KDoreseniRadek polozky={kDoreseni} isDark={isDark} barva={SECTION_COLOR} />
                </div>
            )}
            {karta}
            {!isMobile && (
                <div className="rounded-xl" style={{ ...cardStyle, padding: 16, width: 320, flexShrink: 0 }}>
                    <Nadpis isDark={isDark}>Kontroly</Nadpis>
                    <RadekPanelu isDark={isDark} popis="Chyby" hodnota={zdravi?.souhrn?.chyby ?? '—'} barva={zdravi?.souhrn?.chyby ? CERVENA : null} />
                    <RadekPanelu isDark={isDark} popis="Varování" hodnota={zdravi?.souhrn?.varovani ?? '—'} />
                    <RadekPanelu isDark={isDark} popis="V pořádku" hodnota={zdravi?.souhrn?.ok ?? '—'} />
                    <RadekPanelu isDark={isDark} popis="Zkontrolováno" hodnota={zdravi?.cas ? new Date(zdravi.cas).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' }) : '—'} />
                    <Nadpis isDark={isDark} mt={16}>K dořešení</Nadpis>
                    <KDoreseniSeznam polozky={kDoreseni} isDark={isDark} prazdne={zdravi ? 'Všechny kontroly prošly.' : 'Čekám na kontroly…'} />
                </div>
            )}

            {upravaReportu && (
                <UpravaReportu info={info} isDark={isDark} isMobile={isMobile} inputStyle={inputStyle}
                    onClose={() => setUpravaReportu(false)} onUlozeno={(d) => { setInfo(d); setUpravaReportu(false); }} />
            )}
            {upravaPinu && (
                <UpravaPinuKiosku info={info} isDark={isDark} isMobile={isMobile} inputStyle={inputStyle}
                    onClose={() => setUpravaPinu(false)} onUlozeno={(d) => { setInfo(d); setUpravaPinu(false); }} />
            )}
            {potvrzeni}
        </div>
    );
}

/** Admin PIN skrytého menu kiosku na telefonech rozvozců (aplikace, Kiosek.java). */
function UpravaPinuKiosku({ info, isDark, isMobile, inputStyle, onClose, onUlozeno }) {
    const [pin, setPin] = useState('');
    const [ukladam, setUkladam] = useState(false);
    const [chyba, setChyba] = useState(null);
    const uloz = async (hodnota) => {
        setUkladam(true); setChyba(null);
        try { onUlozeno(await posli('/api/sprava/system', 'PUT', { kiosek_pin: hodnota })); }
        catch (e) { setChyba(e.message); setUkladam(false); }
    };
    return (
        <ModalShell onClose={onClose} isDark={isDark} nadpis="Admin PIN kiosku" ikona={Lock} barva={SECTION_COLOR}
            paticka={<PatickaModalu onZrusit={onClose} onPotvrdit={() => (/^\d{4,8}$/.test(pin) ? uloz(pin) : setChyba('PIN má 4–8 číslic.'))} ukladam={ukladam} isDark={isDark} isMobile={isMobile} />}>
            <div style={{ padding: 16 }}>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Nový PIN (4–8 číslic)</label>
                <input type="password" inputMode="numeric" autoComplete="off" maxLength={8} value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} style={inputStyle} autoFocus />
                <p className="text-xs" style={{ color: tlumene(isDark), margin: '8px 0 0' }}>
                    Na firemním telefonu: 5× rychle ťuknout do pravého horního rohu → PIN → vypnout kiosek (do restartu), navigace, uvolnit telefon.
                </p>
                {info?.kiosek_pin && (
                    <button type="button" onClick={() => uloz(null)} disabled={ukladam} className="text-xs"
                        style={{ marginTop: 10, background: 'none', border: 0, padding: 0, color: CERVENA, cursor: 'pointer' }}>
                        Smazat PIN
                    </button>
                )}
                {chyba && <p className="text-sm" style={{ color: CERVENA, margin: '8px 0 0' }}>{chyba}</p>}
            </div>
        </ModalShell>
    );
}

/** Kontroly po skupinách — stav nese tečka: zelená v pořádku, červená chyba, varování neutrálně (norma §3). */
function SeznamKontrol({ kontroly, isDark, isMobile }) {
    const skupiny = useMemo(() => {
        const m = new Map();
        for (const k of kontroly) { if (!m.has(k.skupina)) m.set(k.skupina, []); m.get(k.skupina).push(k); }
        return [...m.entries()].sort(([a], [b]) => a.localeCompare(b, 'cs'));
    }, [kontroly]);
    const ml = tlumene(isDark);
    const tecka = (stav) => (stav === 'ok' ? ZELENA : stav === 'chyba' ? CERVENA : 'var(--text-secondary)');
    return (
        <div>
            {skupiny.map(([skupina, polozky]) => (
                <div key={skupina} style={{ marginBottom: 6 }}>
                    <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '8px 0 2px' }}>{skupina}</p>
                    {polozky.map((k) => (
                        <div key={k.nazev} className="flex" style={{ gap: 10, padding: isMobile ? '10px 0' : '7px 0', borderTop: `1px solid ${cara(isDark)}`, alignItems: 'baseline' }}>
                            <span aria-label={k.stav} style={{ width: 8, height: 8, borderRadius: 4, background: tecka(k.stav), flexShrink: 0, transform: 'translateY(-1px)' }} />
                            <span style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>{k.nazev}</span>
                                {k.detail && <span className="block" style={{ fontSize: 11.5, color: ml }}>{k.detail}</span>}
                            </span>
                            <span style={{ fontSize: 12.5, color: k.stav === 'chyba' ? CERVENA : 'var(--text-primary)', fontWeight: k.stav === 'chyba' ? 700 : 500, textAlign: 'right', maxWidth: '45%' }}>{k.hodnota}</span>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}

function UpravaReportu({ info, isDark, isMobile, inputStyle, onClose, onUlozeno }) {
    const [email, setEmail] = useState(info?.prijemce_reportu || '');
    const [ukladam, setUkladam] = useState(false);
    const [chyba, setChyba] = useState(null);
    const uloz = async () => {
        setUkladam(true); setChyba(null);
        try { onUlozeno(await posli('/api/sprava/system', 'PUT', { prijemce_reportu: email.trim() || null })); }
        catch (e) { setChyba(e.message); setUkladam(false); }
    };
    return (
        <ModalShell onClose={onClose} isDark={isDark} nadpis="Příjemce zdravotního reportu" ikona={Server} barva={SECTION_COLOR}
            paticka={<PatickaModalu onZrusit={onClose} onPotvrdit={uloz} ukladam={ukladam} isDark={isDark} isMobile={isMobile} />}>
            <div style={{ padding: 16 }}>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>E-mail</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={info?.prijemce_vychozi || ''} style={inputStyle} autoFocus />
                <p className="text-xs" style={{ color: tlumene(isDark), margin: '8px 0 0' }}>Prázdné = výchozí adresa ze serveru ({info?.prijemce_vychozi}).</p>
                {chyba && <p className="text-sm" style={{ color: CERVENA, margin: '8px 0 0' }}>{chyba}</p>}
            </div>
        </ModalShell>
    );
}
